import { Stack } from '@mui/material'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { UserRegSx } from '../../../../components/TextField'
import pxToRem from '../../../../helpers/pxToRem'
import styles from '../index.module.scss'
import { RegModalWrapper } from '../ModalContent'
import { UserUpdate } from '../../../../redux/userRegistrationSlice'
import {
    GoogleMapsTextField,
    PlaceType,
} from '../../../../components/AutoComplete'
import { useEffect, useRef, useState } from 'react'
import { updateUserInfo } from '../../../../firebase/general-apis'
import { updateEmploymentSettingLocationStates } from '../../../../firebase/teacherApi'
import { setErrorMsg } from '../../../../redux/uiSlice'
import z from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import FormInputContact from '../../../../components/FormComponents/FormInputContact'
import {
    selectEmploymentSettingIds,
    selectTeacherContact,
    selectTeacherContactState,
} from '../../../../redux/teacherSlice'
import { operatingCountries, zodContact } from '../../../../schemas/teacherReg'
import { selectUserAuthCheck, selectUserUid } from '../../../../redux/userSlice'
import { getLocation } from '../../MyAccountPage/helper'
import { logger } from '../../../../helpers/logger'

const userLocationSchema = z
    .object({
        address: z.string(),
        country: z.string(),
        homeState: z.string(),
        lat: z.number(),
        long: z.number(),
        postCode: z.string(),
        suburb: z.string(),
    })
    .partial()
    .check((ctx) => {
        if (Object.values(ctx.value).some((el) => el === undefined)) {
            ctx.issues.push({
                code: 'custom',
                message: 'Invalid location',
                input: ctx.value,
            })
        }

        if (ctx.value.country && !operatingCountries.has(ctx.value.country)) {
            ctx.issues.push({
                code: 'custom',
                message: 'We do not operate in this location',
                input: ctx.value.country,
            })
        }
    })

const baseSchema = z.object({
    email: z.email(),
    contact: zodContact,
    addressInput: z.string(),
    homeAddress: userLocationSchema,
})
type FormType = z.infer<typeof baseSchema>

const Contact = () => {
    const dispatch = useDispatch()
    const authed = useSelector(selectUserAuthCheck)
    const userId = useSelector(selectUserUid)
    const contact = useSelector(selectTeacherContact)
    const contactState = useSelector(selectTeacherContactState)

    const settingIds = useSelector(selectEmploymentSettingIds, shallowEqual)

    const {
        control,
        handleSubmit,
        setValue,
        resetField,
        reset,
        formState: { isValid, errors, isDirty },
    } = useForm({
        resolver: zodResolver(baseSchema),
        mode: 'onChange',
    })

    const hasAppliedDefault = useRef(false)
    useEffect(() => {
        if (hasAppliedDefault.current || contact.status !== 'success') return

        hasAppliedDefault.current = true
        reset({
            email: contact.data.email.value,
            contact: contact.data.contact.value || '',
            homeAddress: contact.data.homeLocation,
            addressInput: contact.data.homeLocation.address,
        })
    }, [reset, contact])

    const [homeLocation, setHomeLocation] = useState<PlaceType | null>(null)
    const handleHomeLocationChange = (place: PlaceType | null) => {
        try {
            if (place === null) {
                resetField('homeAddress', {
                    defaultValue: undefined,
                    keepDirty: true,
                })
            } else {
                setValue('homeAddress', getLocation(place), {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                })
            }
            setHomeLocation(place)
        } catch (e) {
            logger.debug(`Invalid location: ${e}`)
        }
    }

    const handleUpdate = async (data: FormType) => {
        try {
            if (!authed || !userId) {
                throw new Error('User is not authenticated!')
            }

            if (!isDirty) return

            let updateData: UserUpdate = {}
            let locationUser = {}
            let locationTeacher = {}

            if (
                data.homeAddress.address !== contact.data?.homeLocation?.address
            ) {
                locationUser = {
                    homeCountry: data.homeAddress.country,
                    homeState: data.homeAddress.homeState,
                }
                locationTeacher = {
                    ...(data.homeAddress.address !==
                    contact.data?.homeLocation.address
                        ? {
                              homeLocation: {
                                  ...data.homeAddress,
                                  state: 'submitted',
                              },
                              setLocation: {
                                  country: data.homeAddress.country,
                                  homeState: data.homeAddress.homeState,
                                  lat: data.homeAddress.lat,
                                  long: data.homeAddress.long,
                              },
                          }
                        : null),
                }
            }

            if (
                !!settingIds &&
                data.homeAddress.homeState !==
                    contact.data?.homeLocation.homeState
            ) {
                await updateEmploymentSettingLocationStates(
                    userId,
                    data.homeAddress.homeState!,
                    settingIds
                )
            }

            updateData = {
                user: {
                    path: '/',
                    data: {
                        ...locationUser,
                        contact: data.contact,
                    },
                },
                teacher: {
                    path: '/',
                    data: {
                        ...locationTeacher,
                        contact: {
                            value: data.contact,
                            state: 'submitted',
                        },
                    },
                },
            }
            await updateUserInfo(userId, updateData)
            // dispatch(updateFields(updateData));
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    return (
        <RegModalWrapper
            title={'Basic Details'}
            handleUpdate={handleSubmit(handleUpdate)}
            state={contactState}
            isAllComplete={isValid}
        >
            <Stack gap={pxToRem(24)} className={styles.modalContainer}>
                <Stack className={styles.titleContainer}>
                    <h5 className={styles.h5}>Contact Information</h5>
                    <p className={styles.p}>
                        Your contact number is shared with schools/centres on
                        the day you're scheduled to work with them.
                    </p>
                </Stack>
                <Stack className={styles.fieldContainer}>
                    <FormInputText
                        control={control}
                        label="Email"
                        disabled
                        name="email"
                        sx={UserRegSx}
                    />
                    <FormInputContact
                        name="contact"
                        label="Contact Number"
                        control={control}
                        sx={UserRegSx}
                        mask={'____ ___ ___'}
                    />
                </Stack>
                <Stack className={styles.titleContainer}>
                    <h5 className={styles.h5}>Location</h5>
                    <p className={styles.p}>
                        Please provide your full home address so we can post you
                        employment-related items if your application is
                        successful.
                    </p>
                </Stack>
                <Controller
                    name="addressInput"
                    control={control}
                    render={(renderProps) => {
                        const homeAddressError =
                            errors.homeAddress?.message ||
                            errors.homeAddress?.address?.message ||
                            errors.homeAddress?.homeState?.message ||
                            errors.homeAddress?.suburb?.message ||
                            errors.homeAddress?.postCode?.message

                        return (
                            <GoogleMapsTextField
                                value={homeLocation}
                                setValue={handleHomeLocationChange}
                                inputValue={renderProps.field.value || ''}
                                setInputValue={renderProps.field.onChange}
                                error={Boolean(homeAddressError)}
                                helperText={homeAddressError}
                                label="Home Address"
                            />
                        )
                    }}
                />
            </Stack>
        </RegModalWrapper>
    )
}

export default Contact
